import React, { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Button } from "../../components/ui/Button";
import { LeftArrowWhite } from "../../assets/images/svg/LeftArrow";
import Verification from "../../assets/images/Verification.png";
import { observer } from "mobx-react";
import { applicationStore } from "../../store/ApplicationStore";
import ProcessingId from "../processing-id";
import { IdentificationTypes } from "src/enums/IdentificationTypes";
import logger from "src/utils/logger";
import { workflowStepIterator } from "src/services/workflowStepIterator";
import { NodeTypes } from "src/enums/NodeTypes";
import { toast } from "react-toastify";
import { parseServiceErrorMessage } from "src/helpers/parseServiceError";
import { useTranslation } from "react-i18next";
import { imageStorage } from "src/utils/imageStorage";
import { FLOW_SHADOW } from "src/theme/flowColorTokens";

const ConfirmPhoto = observer(() => {
    const navigate = useNavigate();
    const location = useLocation();
    const surface = location?.state?.surface;
    const [loading, setLoading] = useState(false);
    const [previewImage, setPreviewImage] = useState(null);
    const { t } = useTranslation();

    useEffect(() => {
        const loadImage = async () => {
            if (surface === "front") {
                const img = await imageStorage.getImage("img");
                setPreviewImage(img);
            } else {
                const backImg = await imageStorage.getImage("backImg");
                setPreviewImage(backImg);
            }
        };
        loadImage();
    }, [surface]);

    const handleSubmit = async () => {
        setLoading(true);

        let formObject = { docs_type: applicationStore.docsType, country: applicationStore.country.toUpperCase() };
        formObject.docs_type = applicationStore.docsType;
        if (surface === "front") {
            formObject.id_frontimage = await imageStorage.getImage("img");
        }
        if (surface === "back") {
            if (applicationStore.docsType === IdentificationTypes.IDENTITY_CARD || applicationStore.docsType === IdentificationTypes.RESIDENCE_PERMIT || applicationStore.docsType === IdentificationTypes.DRIVER_LICENSE) {
                formObject.id_frontimage = await imageStorage.getImage("img");
            }
            formObject.id_backimage = await imageStorage.getImage("backImg");
        }

        try {
            if ((applicationStore.docsType === IdentificationTypes.IDENTITY_CARD || applicationStore.docsType === IdentificationTypes.RESIDENCE_PERMIT || applicationStore.docsType === IdentificationTypes.DRIVER_LICENSE) && applicationStore.documentSurface.front && !applicationStore.documentSurface.back && surface === "back") {

                const response = await workflowStepIterator({
                    access_token: applicationStore.access_token,
                    node_id: applicationStore.workflowNextServiceResponse.node_id,
                    body: {
                        ...formObject,
                    },
                    action_index: applicationStore.workflowNextServiceResponse.index,
                    constextOfStep: { step: NodeTypes.VERIFICATION, "retry": applicationStore.workflowNextServiceResponse.retry }
                })
                if (response.data?.service_results[0].status !== 200) {
                    const rawMessage = response.data?.service_results[0]?.message;
                    const parsedMessage = parseServiceErrorMessage(rawMessage);
                    throw new Error(parsedMessage);
                }
                applicationStore.setWorkflowNextServiceResponse(response.data);
                // if (response.data?.service_results[0]?.data?.liveness_match.verify_completed && response.data?.service_results[0]?.data?.liveness_match.verified === false) {
                //     navigate("/spoof", { replace: true });
                //     return
                // }

            } else if (applicationStore.docsType !== IdentificationTypes.IDENTITY_CARD && applicationStore.docsType !== IdentificationTypes.RESIDENCE_PERMIT && applicationStore.docsType !== IdentificationTypes.DRIVER_LICENSE) {

                const response = await workflowStepIterator({
                    access_token: applicationStore.access_token,
                    node_id: applicationStore.workflowNextServiceResponse.node_id,
                    body: {
                        ...formObject,
                    },
                    action_index: applicationStore.workflowNextServiceResponse.index,
                    constextOfStep: { step: NodeTypes.VERIFICATION, "retry": applicationStore.workflowNextServiceResponse.retry }
                })
                if (response.data?.service_results[0].status !== 200) {
                    const rawMessage = response.data?.service_results[0]?.message;
                    const parsedMessage = parseServiceErrorMessage(rawMessage);
                    throw new Error(parsedMessage);
                }
                applicationStore.setWorkflowNextServiceResponse(response.data);
                // if (response.data?.service_results[0]?.data?.liveness_match.verify_completed && response.data?.service_results[0]?.data?.liveness_match.verified === false) {
                //     navigate("/spoof", { replace: true });
                //     return
                // }
            }

            if (!applicationStore.documentSurface.front && surface === "front") {
                applicationStore.setDocumentSurface({ ...applicationStore.documentSurface, front: true })

                if (applicationStore.documentSurface.front && applicationStore.documentSurface.back) {
                    navigate("/processing-id", { replace: true });
                    return;
                }
                navigate("/take-a-photo", { replace: true });
            }

            if (applicationStore.documentSurface.front && !applicationStore.documentSurface.back && surface === "back") {
                applicationStore.setDocumentSurface({ ...applicationStore.documentSurface, back: true })
                navigate("/processing-id", { replace: true });
                return;
            }
        } catch (error) {
            logger.error(error?.message || t('errors.tryAgainLater'));
            toast.error(error?.message || t('errors.tryAgainLater'));
            setLoading(false);
            navigate("/fail", { replace: true, state: { page: "/take-a-photo" } });
        } finally {
            setLoading(false);
        }
    };

    return (
        <>
            {loading ?
                <ProcessingId /> :
                <div className="absolute inset-0 grid grid-rows-12 h-full text-white">
                    <div className="row-span-3 px-[40px] pt-[47px] bg-Gray950 z-50">
                        <div className="grid grid-cols-3">
                            <Button
                                className=""
                                variant="primaryBack"
                                onClick={() => {
                                    navigate("/take-a-photo", { replace: true });
                                }}
                            >
                                <LeftArrowWhite />
                            </Button>
                            <div className="flex justify-center items-center">
                                <div className="w-[48px] h-[48px] mx-auto">
                                    <figure>
                                        <img alt={t('confirmPhoto.title')} className="" src={Verification} />
                                    </figure>
                                </div>
                            </div>
                        </div>
                        <div className="text-h5 font-bold text-center mt-4 text-Gray50">{t('confirmPhoto.title')}</div>
                        <div className="text-body text-Gray50 text-center mt-2">{t('confirmPhoto.description')}</div>
                    </div>
                    <div className="row-span-9 grid grid-rows-24 bg-Gray950">
                        <div className="relative row-span-11">
                            <figure>
                                <img
                                    alt="LcStartImg"
                                    className=""
                                    src={previewImage || ""}
                                />
                            </figure>
                        </div>
                        <div className="absolute top-0 left-0 w-full h-full z-10">
                            <div className="relative h-full w-full overflow-hidden">
                                <div className={`absolute left-1/2 top-42% flex flex-row justify-center items-center bg-transparent rounded-[16px] ${applicationStore.docsType === IdentificationTypes.PASSPORT ? "w-[320px] h-[220px]" : "h-[198px] w-[313px]"} `}
                                    style={{
                                        boxShadow: FLOW_SHADOW.vignettePhone,
                                        transform: "translate(-50%, -50%)",
                                    }}
                                >
                                </div>
                            </div>
                        </div>
                        <div className="row-span-13 z-20">
                            <div className="flex justify-center flex-col items-center">
                                <div className="w-full text-h5 font-bold text-center text-Gray50 pb-16 px-8">
                                    <div className="flex justify-start flex-col items-center bg-Gray950 h-full pt-10">

                                        <Button className="mt-1 w-[300px]" variant="primaryWithoutIcon" onClick={() => handleSubmit()}>
                                            {t('confirmPhoto.usePhoto')}
                                        </Button>

                                        <Button onClick={() => navigate("/take-a-photo", { replace: true })} className="mt-2 w-[300px]" variant="secondaryWithoutIcon">{t('confirmPhoto.retakePhoto')}</Button>

                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            }
        </>
    )
})

export default ConfirmPhoto
